import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import { startRegistration, type PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';
import type { PasskeyListItem } from '@avisoma/shared';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../catalyst/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../catalyst/table';
import { useAuth } from '../hooks/useAuth';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { formatDateTime } from '../lib/dateFormat';
import backend from '../services/backend';
import { hasWebAuthn, isPasskeyCeremonyCancelled, shouldShowPasskeyButton } from './passkeys';

export default function Security() {
  useDocumentTitle('Security');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const webAuthn = hasWebAuthn();

  const availableQuery = useQuery({
    queryKey: ['passkeys', 'available', user?.id],
    queryFn: () => backend.passkeysAvailable().then((res) => res.data),
  });
  const listQuery = useQuery({
    queryKey: ['passkeys', 'list', user?.id],
    queryFn: () => backend.listPasskeys().then((res) => res.data),
    enabled: availableQuery.data?.enabled === true,
  });

  const showAdd = shouldShowPasskeyButton({
    hasWebAuthn: webAuthn,
    available: availableQuery.data?.enabled === true,
  });

  const add = useMutation({
    mutationFn: async () => {
      const options = await backend.passkeyRegisterOptions();
      const credential = await startRegistration({
        optionsJSON: options.data as unknown as PublicKeyCredentialCreationOptionsJSON,
      });
      await backend.passkeyRegisterVerify(credential);
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['passkeys'] });
    },
    onError: (error) => {
      setError(isPasskeyCeremonyCancelled(error) ? 'Cancelled' : 'Could not add passkey');
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => backend.deletePasskey(id),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['passkeys'] });
    },
    onError: () => {
      setError('Could not remove passkey');
    },
  });

  return (
    <div>
      <PageHeader
        title="Security"
        description="Manage passkeys for this account."
        icon={<ShieldCheckIcon className="size-6" />}
      >
        {showAdd && (
          <Button
            color="blue"
            className="shrink-0 whitespace-nowrap"
            onClick={() => add.mutate()}
            disabled={add.isPending}
          >
            {add.isPending ? 'Adding…' : 'Add passkey'}
          </Button>
        )}
      </PageHeader>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 dark:bg-red-950/40">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {availableQuery.isSuccess && availableQuery.data.enabled === false ? (
        <div className="text-center text-xl font-extralight text-zinc-500 dark:text-zinc-400 py-12">
          Passkeys are not enabled on this host.
        </div>
      ) : (
        <PasskeysBody
          loading={availableQuery.isLoading || (availableQuery.data?.enabled === true && listQuery.isPending)}
          isError={availableQuery.isError || listQuery.isError}
          passkeys={listQuery.data ?? []}
          removingId={remove.isPending ? (remove.variables ?? null) : null}
          onRemove={(id) => remove.mutate(id)}
        />
      )}
    </div>
  );
}

function PasskeysBody({
  loading,
  isError,
  passkeys,
  removingId,
  onRemove,
}: {
  loading: boolean;
  isError: boolean;
  passkeys: PasskeyListItem[];
  removingId: string | null;
  onRemove: (id: string) => void;
}) {
  if (loading) {
    return <div className="text-center text-zinc-500 dark:text-zinc-400 py-12">Loading…</div>;
  }

  if (isError) {
    return (
      <div className="bg-white dark:bg-zinc-800 shadow rounded-lg p-6">
        <div className="text-center text-red-600 dark:text-red-400">Failed to load passkeys</div>
      </div>
    );
  }

  if (passkeys.length === 0) {
    return (
      <div className="text-center text-xl font-extralight text-zinc-500 dark:text-zinc-400 py-12">
        No passkeys yet.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-zinc-800 dark:shadow-zinc-950/30">
      <Table className="px-4">
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Created</TableHeader>
            <TableHeader>Last used</TableHeader>
            <TableHeader>
              <span className="sr-only">Actions</span>
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {passkeys.map((passkey) => (
            <TableRow key={passkey.id}>
              <TableCell className="font-medium">{passkey.name}</TableCell>
              <TableCell>{formatDateTime(passkey.createdAt)}</TableCell>
              <TableCell>
                {passkey.lastUsedAt ? (
                  formatDateTime(passkey.lastUsedAt)
                ) : (
                  <span className="text-zinc-500 dark:text-zinc-400">never</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <Button outline onClick={() => onRemove(passkey.id)} disabled={removingId === passkey.id}>
                  {removingId === passkey.id ? 'Removing…' : 'Remove'}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
